import { createSelector } from "@reduxjs/toolkit";
import {
    ConferenceParticipant,
    ConferenceParticipantStatus,
    CONFERENCE_PARTICIPANT_STATUS,
    TASK_TYPES,
    TASK_RESERVATION_STATUSES,
} from "@regal-voice/shared-types";
import memoize from "lodash/memoize";

import { getLogger } from "Services/LoggingService";
import { selectWorkerUri } from "Services/state/agent/AgentInformationSlice";
import { selectActiveConferenceTask } from "Services/state/conferences";
import {
    isTaskACall,
    isPendingInboundConferenceTask,
    isTransferCallTask,
    isTakeOverCall,
    isPendingInboundTransferTask,
} from "Services/Task.service";

import { ALLOWED_STATUSES, DISCONNECTED_STATUSES, TASK_STATUS_DISPLAY } from "./ConferenceSnapshotConstants";

import type { RVTask, TaskReservationStatus } from "@regal-voice/shared-types";

const logger = getLogger("ConferenceSnapshots");

const checkDisconnected = (status: ConferenceParticipantStatus) => DISCONNECTED_STATUSES.includes(status);

export const selectConferenceSnapshot = (state: RootState) => state.conferenceSnapshot;

const particpantFromTask = (activeConferenceTask: RVTask<"voice">): ConferenceParticipant<"contact"> => {
    const joinTime = new Date(activeConferenceTask.reservationAcceptedAt || activeConferenceTask.dateCreated).getTime();
    return {
        callSid: activeConferenceTask.attributes.joinCallSid,
        hold: false,
        joinedAt: joinTime,
        label: activeConferenceTask.attributes.name,
        // types say we dont have a null action, but the server says we do....
        // managerAction: null,
        muted: false,
        phoneNumber: activeConferenceTask.attributes.contactPhone,
        profileId: activeConferenceTask.attributes.profileId,
        status: "ringing",
        taskSid: activeConferenceTask.taskSid,
        type: "contact",
    };
};

export const selectConferenceSnapshotSelf = createSelector(
    selectConferenceSnapshot,
    selectWorkerUri,
    (snapshot, uri) => {
        const self = snapshot.participants.find((participant) => {
            // if the agent is added to call repeatedly we only want the connected one
            const isDisconnected = checkDisconnected(participant.status);
            return "callerUri" in participant && participant.callerUri.includes(uri || "") && !isDisconnected;
        }) as ConferenceParticipant<"internal"> | undefined;
        return self;
    }
);

// this is the incoming participant from before the agent picks up
// the agent isn't getting snapshots because they aren't in the conference yet
// but we need to display who is calling in the task card
export const selectSnapshotFallbackTaskParticipant = createSelector(
    selectActiveConferenceTask,
    (activeConferenceTask): ConferenceParticipant<"contact"> | undefined => {
        if (!!activeConferenceTask) {
            return particpantFromTask(activeConferenceTask as unknown as RVTask<"voice">);
        }
    }
);

/**
 * Participant list after filtering out disconnected participants and managers in listen mode.
 */
export const selectConferenceSnapshotActiveParticipants = createSelector(
    selectConferenceSnapshot,
    selectConferenceSnapshotSelf,
    selectSnapshotFallbackTaskParticipant,
    selectActiveConferenceTask,
    (snapshot, selfParticipant, fallbackParticipant, activeConferenceTask) => {
        const participants = snapshot.participants.filter((participant) => {
            const isSelf =
                selfParticipant && "callerUri" in participant && participant.callerUri === selfParticipant.callerUri;
            const isDisconnected = checkDisconnected(participant.status);
            const isListeningManager = "managerAction" in participant && participant.managerAction === "listen";

            return !isSelf && "status" in participant && !isDisconnected && !isListeningManager;
        });

        if (fallbackParticipant) {
            const hasNoValidParticipants = participants.length === 0;
            const isDifferentConference =
                snapshot.conferenceFriendlyId !== activeConferenceTask?.attributes.conferenceFriendlyId;

            if (hasNoValidParticipants || isDifferentConference) {
                return [fallbackParticipant];
            }
        }
        return participants;
    }
);

export const selectConferenceSnapshotSingularActiveParticipant = createSelector(
    selectConferenceSnapshotActiveParticipants,
    (participants) => {
        return participants.find((participant) =>
            ALLOWED_STATUSES.includes(participant.status as ConferenceParticipantStatus)
        );
    }
);

/**
 * All of the Barging participants (Managers)
 */
export const selectConferenceSnapshotBarges = createSelector(selectConferenceSnapshot, (snapshot) => {
    return snapshot.participants.filter(
        (participant) => participant.type !== "queue" && participant.managerAction == "barge"
    );
});

/**
 * The main contact of the conference
 */
export const selectConferenceSnapshotContactParticipant = createSelector(
    selectConferenceSnapshotActiveParticipants,
    (activeParticipants) => {
        const contact = activeParticipants.find(
            (participant: ConferenceParticipant<"unknown">) => participant?.type === "contact"
        );
        return contact as ConferenceParticipant<"contact"> | undefined;
    }
);

/**
 * The main contact of the conference filtered by task's taskSid
 */
export const selectConferenceSnapshotContactParticipantForTask = createSelector(
    selectConferenceSnapshotActiveParticipants,
    (activeParticipants) => {
        return memoize((task?: RVTask) => {
            if (!task?.taskSid || !activeParticipants?.length) {
                return undefined;
            }
            const { taskSid, attributes } = task;

            const taskContactMatchingAttributes: [keyof ConferenceParticipant<"contact">, string][] = [
                ["taskSid", taskSid],
                attributes?.originating_task_sid &&
                    isTransferCallTask(task) && ["taskSid", attributes.originating_task_sid], // for call transfers
                attributes?.originating_task_sid &&
                    isTakeOverCall(task) && ["taskSid", attributes.originating_task_sid], // for takeover calls
                attributes?.conferenceFriendlyId && ["callSid", attributes.conferenceFriendlyId], // this is not always up to date, but it should be the best one
            ].filter(Boolean);

            const contact = activeParticipants.find(
                (participant) =>
                    participant.type === "contact" &&
                    taskContactMatchingAttributes.some(([attribute, value]) => participant[attribute] === value)
            );

            return contact as ConferenceParticipant<"contact"> | undefined;
        });
    }
);

/**
 * Any and all of the agents
 */

/**
 * Conference State
 */
export const selectSnapShotConferenceSid = createSelector(
    selectConferenceSnapshot,
    (snapshot) => snapshot.conferenceSid
);

export const selectSnapshotConferenceFriendlyID = createSelector(
    selectConferenceSnapshot,
    (snapshot) => snapshot.conferenceFriendlyId
);

export const selectSnapshotConferenceIsConnected = createSelector(
    selectConferenceSnapshotActiveParticipants,
    (participants) => participants.some((p) => p.status === CONFERENCE_PARTICIPANT_STATUS.inProgress)
);

export const selectIsConferenceRecording = createSelector(selectConferenceSnapshot, (snapshot) => snapshot.recording);

export const selectConferenceSnapshotAgentCallSid = createSelector(
    selectConferenceSnapshotSelf,
    (agent) => agent?.callSid
);

export const selectConferenceSnapshotContactCallSid = createSelector(
    selectConferenceSnapshotContactParticipant,
    (contact) => contact?.callSid
);

export const selectSnapshotIsPowerDialTaskWaiting = createSelector(
    selectActiveConferenceTask,
    (activeConferenceTask) =>
        activeConferenceTask &&
        activeConferenceTask.status === "pending" &&
        activeConferenceTask.attributes.autoAnswer &&
        activeConferenceTask.attributes.taskType === TASK_TYPES.OUTBOUND_AUTO_ANSWER // "regal" is used for sms
);

const selectSnapshotInboundCallTaskWaiting = createSelector(selectActiveConferenceTask, (activeConferenceTask) => {
    return !!(activeConferenceTask && isPendingInboundConferenceTask(activeConferenceTask));
});

type StatusDisplayProps = {
    status?: TaskReservationStatus;
    isCall: boolean;
};

const getBasicTaskStatusDisplay = ({ status, isCall }: StatusDisplayProps) => {
    switch (status) {
        case TASK_RESERVATION_STATUSES.ACCEPTED:
            return isCall ? TASK_STATUS_DISPLAY.LIVE : TASK_STATUS_DISPLAY.ACTIVE;
        case TASK_RESERVATION_STATUSES.WRAPPING:
            return TASK_STATUS_DISPLAY.WRAP_UP;
        case TASK_RESERVATION_STATUSES.COMPLETED:
            return TASK_STATUS_DISPLAY.COMPLETED;
        case TASK_RESERVATION_STATUSES.CANCELED:
            return TASK_STATUS_DISPLAY.CANCELED;
        case TASK_RESERVATION_STATUSES.PENDING:
            return isCall ? TASK_STATUS_DISPLAY.RINGING : "";
        default:
            return "";
    }
};

export const selectSnapshotTaskStatus = createSelector(
    selectConferenceSnapshotContactParticipantForTask,
    selectConferenceSnapshotActiveParticipants,
    (contactParticpantSelector, _activeParticipants) =>
        memoize((task?: RVTask) => {
            if (!task) {
                logger.warn("No task provided to makeSelectSnapshotTaskStatus");
                return "Invalid Task";
            }

            if (task.status === TASK_RESERVATION_STATUSES.WRAPPING) {
                return TASK_STATUS_DISPLAY.WRAP_UP;
            }

            const contactParticpant = contactParticpantSelector(task);
            const isCall = isTaskACall(task);

            // return the status solely based on the task
            if (task?.taskSid !== contactParticpant?.taskSid) {
                return getBasicTaskStatusDisplay({ status: task?.status, isCall });
            }

            // we need to take the contact into consideration
            if (contactParticpant?.callSid) {
                if (
                    contactParticpant?.status == CONFERENCE_PARTICIPANT_STATUS.initiated ||
                    contactParticpant?.status == CONFERENCE_PARTICIPANT_STATUS.ringing
                ) {
                    return TASK_STATUS_DISPLAY.RINGING;
                }

                return getBasicTaskStatusDisplay({ status: task?.status, isCall });
            } else {
                if (
                    contactParticpant?.status == CONFERENCE_PARTICIPANT_STATUS.initiated ||
                    contactParticpant?.status == CONFERENCE_PARTICIPANT_STATUS.ringing
                ) {
                    return isPendingInboundTransferTask(task)
                        ? TASK_STATUS_DISPLAY.RINGING
                        : TASK_STATUS_DISPLAY.CONNECTING;
                } else {
                    return TASK_STATUS_DISPLAY.WRAP_UP;
                }
            }
        })
);

type TimeProps = {
    task?: RVTask;
    // when we remove the rollout flag we should stop using an array here
    // conferenceParticipants: Array<ConferenceParticipant<"unknown">>;
    isCall: boolean;
    selfParticpant?: ConferenceParticipant<"internal">;
};

const detemineTimeForTaskCard = (props: TimeProps) => {
    const { task, selfParticpant, isCall } = props;
    let value: string | undefined;

    switch (task?.status) {
        case "pending":
            value = task.dateCreated || "";
            break;
        case "accepted":
            if (isCall) {
                value = `${selfParticpant?.joinedAt || 0}`;
                // @ts-expect-error this will be fixed when we remove the rollout flag
                const timestampCheck = selfParticpant?.startTimestamp;
                // use the accepted time if there are no contacts in the conference
                // - we always fall back to reservationCreatedDate outside of the if
                const firstContactStartTimestamp = timestampCheck
                    ? new Date(timestampCheck).toString()
                    : task.reservationAcceptedAt;
                value = firstContactStartTimestamp;
            }
            // if it is not a call we will fallback to using a task value
            value = value || task.reservationAcceptedAt || task.reservationCreatedDate;
            break;
        default:
            value = isCall ? task?.reservationUpdatedDate : task?.reservationCreatedDate;
    }

    if (!value) {
        logger.error(
            `Could not find proper start time for task time display, using fallback value. Task data: ${JSON.stringify(
                task
            )}`
        );
        value = new Date().toString();
    }

    return value;
};

export const selectSnapshotTimeDisplay = createSelector(selectConferenceSnapshotSelf, (self) =>
    memoize((task?: RVTask) => {
        const isCall = isTaskACall(task);

        const timeDisplay = detemineTimeForTaskCard({
            task,
            isCall,
            selfParticpant: self,
        });

        return {
            timeDisplay,
        };
    })
);

export const selectSnapshotShowPanel = createSelector(
    selectActiveConferenceTask,
    selectSnapshotIsPowerDialTaskWaiting,
    selectSnapshotInboundCallTaskWaiting,
    (activeConferenceTask, inboundCallTaskWaiting, isPowerDialTaskWaiting) => {
        const show = !!(isPowerDialTaskWaiting || inboundCallTaskWaiting || activeConferenceTask?.status == "accepted");

        return show;
    }
);
